import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { db } from "../firebase.config.js";
import { doc, onSnapshot } from "firebase/firestore";
import { normalizeUpdatedAtToDate } from "../utils/dateFormat";
import pLogo from "../assets/p-logo.png";
import useFetchData from "../components/useFetchDatav2";
import "./EtlRun.css";

const ETL_RUNS_COLLECTION = "etl_runs";
const VENDORBILLS_ID = "vendorbills";
const COOLDOWN_MS = 5 * 60 * 1000;

const PLogoIcon = ({ size = 30 }) => (
  <img
    src={pLogo}
    alt="Protractor Logo"
    width={size}
    height={size}
    style={{ objectFit: "contain" }}
  />
);

const CustomToggle = React.forwardRef(function CustomToggle(
   { children, onClick },
   ref
 ) {
   return (
     <Link to="" ref={ref} onClick={(e) => { e.preventDefault(); onClick(e);}} className="dropdown-link">
       {children}
     </Link>
   );
});

const EtlRunTrigger = React.memo(function EtlRunTrigger({
   showSpinner,
   status,
 }) {
   const containerStyle = {
     position: "relative",
     display: "inline-flex",
     alignItems: "center",
     justifyContent: "center",
     width: "39px",
     height: "39px",
   };

   const spinnerStyle = {
     position: "absolute",
     top: 0,
     left: 0,
     width: "39px",
     height: "39px",
     zIndex: 1,
   };

   const spinnerClass =
     status === "FAILED" ? "text-danger" : "text-primary";
   console.log("LOGO RENDER", status, showSpinner);
   return (
     <div style={containerStyle}>
       <div
         className={`spinner-border ${spinnerClass} etl-run-spinner`}
         role="status"
         style={{
           ...spinnerStyle,
           opacity: showSpinner ? 1 : 0,
           pointerEvents: "none",
         }}
       />
       <div style={{ position: "relative", zIndex: 2 }}>
         <PLogoIcon size={30} />
       </div>
     </div>
   );
});


const formatElapsedTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return null;
  const totalMinutes = Math.floor(diffMs / (60 * 1000));
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) return `${totalHours} hr`;
  const totalDays = Math.floor(totalHours / 24);
  return `${totalDays} days`;
};

export default function EtlRun() {
  // const [dropdownOpen, setDropdownOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [lastRequestedAt, setLastRequestedAt] = useState(null);
  const [lastSuccessAt, setLastSuccessAt] = useState(null);
  const [lastFailureAt, setLastFailureAt] = useState(null);
  const { fetchData } = useFetchData();
  const dropdownContainerRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const [, cooldownTick] = useState(0);
  const lastRequestedAtMs = lastRequestedAt?.getTime() ?? null;

  // Perry added to let user know how much time is left in the cool down
  const formatRemainingTime = (ms) => {
    if (ms == null || ms <= 0) return null;

    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) return `${minutes} min ${seconds}s`;
    return `${seconds}s`;
  };

  // Firestore: listen to etl_runs/vendorbills
  useEffect(() => {
    const docRef = doc(db, ETL_RUNS_COLLECTION, VENDORBILLS_ID);
    unsubscribeRef.current = onSnapshot(
      docRef,
      (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          setStatus(null);
          setLastRequestedAt(null);
          setLastSuccessAt(null);
          setLastFailureAt(null);
          return;
        }
        setStatus(data.status ?? null);
        setLastRequestedAt(normalizeUpdatedAtToDate(data.lastRequestedAt));
        setLastSuccessAt(normalizeUpdatedAtToDate(data.lastSuccessAt));
        setLastFailureAt(normalizeUpdatedAtToDate(data.lastFailureAt));
      },
      (err) => {
        console.error("EtlRun snapshot error:", err);
        setStatus(null);
        setLastRequestedAt(null);
        setLastSuccessAt(null);
        setLastFailureAt(null);
      }
    );
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  // Refresh and spinner logic: based on current time - lastSuccessAt
  const lastSuccessAtMs = lastSuccessAt?.getTime() ?? null;

  const nowMs = Date.now();
  
  //const timeSinceLastSuccessMs = lastSuccessAtMs != null ? nowMs - lastSuccessAtMs : null;

  const refreshEnabled =
    status === "IDLE" &&
    lastRequestedAtMs != null &&
    nowMs - lastRequestedAtMs > COOLDOWN_MS;

  // Perry added cooldown time to trigger re-render every second during cooldown
  useEffect(() => {
    if (refreshEnabled) return;

    const interval = setInterval(() => {
      cooldownTick((n) => n + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [refreshEnabled]);

  const showSpinner = status === "RUNNING";

  //Perry added a cool down where null->no success yet, >0->cooldown active, 0->refresh allowed
  const cooldownRemainingMs =
    lastRequestedAtMs != null
      ? Math.max(
          0,
          COOLDOWN_MS - (nowMs - lastRequestedAtMs)
        )
      : null;

  const handleDropdownToggle = (nextShow) => {
    setDropdownOpen(nextShow);
  };

  const handleRefreshClick = async () => {
      const url = "/vendorbills.sz";
      const method = "POST";
      try {
         const { responseData, fetchLoading, fetchError } = await fetchData(url, method, null, "form-data");
         console.log("RESPONSEDATA:", responseData);
      } catch (error) {
         console.error("Error fetching data:", error);
      }
  };

  const lastSuccessElapsed = formatElapsedTime(lastSuccessAt);
  const lastFailureElapsed = formatElapsedTime(lastFailureAt);

  // Determine right-side label and elapsed time
  const latestEvent =
    lastSuccessAt && lastFailureAt
      ? lastSuccessAt > lastFailureAt
        ? { label: "Success", date: lastSuccessAt }
        : { label: "Failure", date: lastFailureAt }
      : lastSuccessAt
      ? { label: "Success", date: lastSuccessAt }
      : lastFailureAt
      ? { label: "Failure", date: lastFailureAt }
      : null;

  const statusLabel = latestEvent?.label ?? "";
  const statusElapsed = latestEvent
    ? formatElapsedTime(latestEvent.date)
    : null;

  return (
    <Dropdown
      ref={dropdownContainerRef}
      // show={dropdownOpen}
      // onToggle={handleDropdownToggle}
      align="end"
      className="etl-run-dropdown dropdown-notification ms-3 ms-xl-4 d-flex align-items-center"
    >
      <Dropdown.Toggle as={CustomToggle}><EtlRunTrigger showSpinner={showSpinner} status={status}/></Dropdown.Toggle>
      <Dropdown.Menu
        ref={dropdownMenuRef}
        className="mt-10-f me--10-f"
        style={{
          width: "380px",
          maxHeight: "min(90vh, calc(100vh - 2rem))",
          overflow: "hidden",
        }}
      >
        <div className="dropdown-menu-header">
          <h6 className="dropdown-menu-title">Protractor Vendor Bills</h6>
        </div>

        <div className="px-3 py-2 small text-muted">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              Last refresh:{" "}
              {statusElapsed && (
                <span className="fw-semibold text-dark">{statusElapsed}</span>
              )}
            </span>
            <span className="fw-semibold text-dark">{statusLabel}</span>
          </div>
        </div>

        {/* <div className="dropdown-menu-footer d-flex justify-content-end p-2"> */}
        <div className="dropdown-menu-footer d-flex align-items-center p-2">
          {!refreshEnabled && cooldownRemainingMs > 0 && (
            <span className="small text-muted">
              Refresh available in{" "}
              <strong>{formatRemainingTime(cooldownRemainingMs)}</strong>
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-primary ms-auto"
            disabled={!refreshEnabled}
            onClick={handleRefreshClick}
          >
            Refresh
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
